import { Alert, Button, Divider, Group, Loader, Modal, Stack, Switch, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserConsentsApi } from '../../../apis/UserConsentsApi/UserConsentsApi.ts';
import { useUserConsent } from '../../../hooks/useUserConsent.ts';
import { useSessionProvider } from '../../../provider/sessionContext.tsx';

interface CookiePreferencesModalProps {
    opened: boolean;
    onClose: () => void;
    onSave?: () => void;
}

const CookiePreferencesModal: React.FC<CookiePreferencesModalProps> = ({ opened, onClose, onSave }) => {
    const { t } = useTranslation();
    const { consent, saveConsent } = useUserConsent();
    const { currentUser } = useSessionProvider();
    const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const loadPreferences = async () => {
            if (!opened) return;

            setIsLoading(true);
            try {
                // First try to use consent from hook
                if (consent) {
                    setAnalyticsEnabled(consent.analyticsCookies);
                } else if (currentUser?.hasProfile) {
                    // If no consent in hook, fetch directly from API
                    const preferences = await UserConsentsApi.getPreferences();
                    setAnalyticsEnabled(preferences.analyticsCookies || false);
                } else {
                    // No user, set defaults
                    setAnalyticsEnabled(false);
                }
            } catch (error) {
                console.error('Failed to load cookie preferences:', error);
                // On error, set defaults
                setAnalyticsEnabled(false);
            } finally {
                setIsLoading(false);
            }
        };

        loadPreferences();
    }, [opened, consent, currentUser?.hasProfile]);

    const handleSave = async () => {
        console.log('CookiePreferencesModal: handleSave called, calling onSave');
        // Immediately notify parent that save was clicked (show button right away)
        if (onSave) {
            console.log('CookiePreferencesModal: Calling onSave callback');
            onSave();
        } else 
            console.warn('CookiePreferencesModal: onSave is not defined');
        
        onClose();

        // Save to database in background (don't wait for it)
        try {
            await saveConsent({
                essentialCookies: true,
                analyticsCookies: analyticsEnabled,
            });
            console.log('CookiePreferencesModal: Consent saved successfully');
        } catch (error) {
            console.error('Failed to save cookie preferences:', error);
            // Even if save fails, button will remain visible (user can try again)
        }
    };

    const handleAcceptAll = async () => {
        // Set all cookies to enabled
        setAnalyticsEnabled(true);

        if (onSave)
            onSave();
        
        onClose();

        // Save to database in background
        try {
            await saveConsent({
                essentialCookies: true,
                analyticsCookies: true,
            });
        } catch (error) {
            console.error('Failed to save cookie preferences:', error);
        }
    };

    const handleRejectAll = async () => {
        // Set all non-essential cookies to disabled
        setAnalyticsEnabled(false);

        if (onSave)
            onSave();
        
        onClose();

        // Save to database in background
        try {
            await saveConsent({
                essentialCookies: true,
                analyticsCookies: false,
            });
        } catch (error) {
            console.error('Failed to save cookie preferences:', error);
        }
    };

    return (
        <Modal opened={opened} onClose={onClose} title={t('cookieConsent.preferences.title')} size="md" centered zIndex={1100}>
            {isLoading ? (
                <Stack align="center" gap="md" py="xl">
                    <Loader size="md" />
                    <Text size="sm" c="dimmed">
                        {t('cookieConsent.preferences.loading') || 'Loading preferences...'}
                    </Text>
                </Stack>
            ) : (
                <Stack gap="lg">
                    <Text size="sm" c="dimmed">
                        {t('cookieConsent.preferences.description')}
                    </Text>

                    {/* Essential Cookies */}
                    <Stack gap="xs">
                        <Group justify="space-between">
                            <Stack gap={4}>
                                <Text fw={500}>{t('cookieConsent.preferences.essential.title')}</Text>
                                <Text size="xs" c="dimmed">
                                    {t('cookieConsent.preferences.essential.description')}
                                </Text>
                            </Stack>
                            <Switch checked disabled />
                        </Group>
                        <Alert icon={<IconInfoCircle size="1rem" />} color="blue" variant="light">
                            <Text size="xs">{t('cookieConsent.preferences.essential.info')}</Text>
                        </Alert>
                    </Stack>

                    <Divider />

                    {/* Analytics Cookies */}
                    <Stack gap="xs">
                        <Group justify="space-between">
                            <Stack gap={4}>
                                <Text fw={500}>{t('cookieConsent.preferences.analytics.title')}</Text>
                                <Text size="xs" c="dimmed">
                                    {t('cookieConsent.preferences.analytics.description')}
                                </Text>
                            </Stack>
                            <Switch checked={analyticsEnabled} onChange={e => setAnalyticsEnabled(e.currentTarget.checked)} />
                        </Group>
                    </Stack>

                    <Divider />

                    <Button onClick={handleSave}>
                            {t('cookieConsent.preferences.save')}
                        </Button>
                    <Group gap="sm" grow>
                        <Button variant="default" onClick={handleRejectAll}>
                            {t('cookieConsent.preferences.rejectAll')}
                        </Button>
                        
                        <Button onClick={handleAcceptAll}>
                            {t('cookieConsent.preferences.acceptAll')}
                        </Button>
                    </Group>
                </Stack>
            )}
        </Modal>
    );
};

export default CookiePreferencesModal;
